import { useTranslation } from "react-i18next";
import { AtSign, Check, Facebook, Instagram, Linkedin, Plug } from "lucide-react";
import type { Platform } from "@marketing/shared";
import { useConnectedAccounts } from "@/lib/social";

const PLATFORM_ICON: Partial<Record<Platform, typeof Facebook>> = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  threads: AtSign,
};

// =============================================================================
// Which connected pages a post should go to.
//
// Until now everything went everywhere: the API has always taken
// `socialAccountIds`, but nothing in the dashboard ever set it, so a brand
// with Facebook, Instagram and LinkedIn got all three whether or not the post
// suited all three.
//
// Empty means every page, which is both the API's existing default and the
// right one — a brand with one page should never have to tick it. The copy
// says so rather than leaving an empty selection looking like a mistake.
// =============================================================================

export function PagePicker({
  brandId,
  value,
  onChange,
  label,
}: {
  brandId: string | null;
  /** Empty = every connected page. */
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const accounts = useConnectedAccounts();

  const pages = (accounts.data ?? []).filter(
    (a) => a.brand.id === brandId && a.status !== "disconnected",
  );

  // One page is not a choice; showing a checkbox for it is noise.
  if (pages.length < 2) return null;

  const all = value.length === 0;

  function toggle(id: string) {
    if (all) {
      // First tick turns "everything" into "everything except this one",
      // which is what someone unticking from a full list means.
      onChange(pages.filter((p) => p.id !== id).map((p) => p.id));
      return;
    }
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    // Back to every page rather than none: a post going nowhere is not a
    // thing anyone wants, and the API would reject it.
    onChange(next.length === pages.length ? [] : next);
  }

  return (
    <div>
      <span className="label">{label ?? t("pages.title")}</span>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onChange([])}
          aria-pressed={all}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors ${
            all
              ? "border-primary-600 bg-primary-600 text-white"
              : "border-border bg-white text-muted hover:border-primary-300 hover:text-ink"
          }`}
        >
          {all ? <Check size={13} /> : null}
          {t("pages.all")}
        </button>

        {pages.map((page) => {
          const Icon = PLATFORM_ICON[page.platform] ?? Plug;
          const on = all || value.includes(page.id);
          return (
            <button
              key={page.id}
              type="button"
              onClick={() => toggle(page.id)}
              aria-pressed={on}
              title={page.displayName}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors ${
                on
                  ? "border-primary-300 bg-primary-50 text-primary-800"
                  : "border-border bg-white text-muted hover:border-primary-300 hover:text-ink"
              }`}
            >
              <Icon size={13} />
              <span className="max-w-[10rem] truncate">{page.displayName}</span>
            </button>
          );
        })}
      </div>
      <span className="hint">{all ? t("pages.allHint", { count: pages.length }) : t("pages.someHint", { count: value.length })}</span>
    </div>
  );
}
